import { useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    BoxProps,
    IconButton,
    InternalStandardProps,
} from "@mui/material";
import { PlayerSeekAction } from "JS/Helpers";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import RepeatOneIcon from "@mui/icons-material/RepeatOne";
import RepeatOneOn from "@mui/icons-material/RepeatOneOn";
import forward30Icon from "Images/Content/forward-30.png";
import forward2mIcon from "Images/Content/forward-2m.png";
import rewind30Icon from "Images/Content/rewind-30.png";
import rewind15Icon from "Images/Content/rewind-15.png";
import forward30WIcon from "Images/Content/forward-30-w.png";
import forward2mWIcon from "Images/Content/forward-2m-w.png";
import rewind30WIcon from "Images/Content/rewind-30-w.png";
import rewind15WIcon from "Images/Content/rewind-15-w.png";
import Shuffle from "@mui/icons-material/Shuffle";
import ShuffleOn from "@mui/icons-material/ShuffleOn";
import Repeat from "@mui/icons-material/Repeat";
import RepeatOn from "@mui/icons-material/RepeatOn";
import { useMiniPlayer } from "JS/React/Hooks/MiniPlayer";
import { getCurrentUrl } from "JS/Helpers/MiniPlayerHelper";
import { useThemeModeContext } from "JS/React/Context/ThemeModeProvider";

const ControlIconsBox = styled(Box)(({ theme }) => ({
    maxWidth: "40px",
    display: "flex",
    color: theme.palette.grey[600],
}));

const StyledPlayerIcon = styled("img")(({ theme }) => ({
    height: "35px",
    color: theme.palette.grey[600],
}));

const StyledMuiIcon = styled(Box)(({ theme }) => ({
    "& .MuiSvgIcon-root": {
        height: "35px",
        color: theme.palette.grey[600],
    },
}));

const StyledIconButton = styled(IconButton)(() => ({
    padding: "8px 0",
}));

const StyledPictureInPictureIcon = styled(PictureInPictureAltIcon)(({ theme }) => ({
    height: "35px",
    color: theme.palette.grey[600],
}));

export const PlayerActionBtns = (props: PlayerActionBtnsProps) => {
    const {
        isLoop,
        isShuffle,
        isMultiLoop,
        handleChangeSeek,
        onClickLoop,
        onClickMiniPlayer,
        onClickShuffle,
        onClickMultiLoop,
    } = props;

    const { miniPlayer, setGlobalMiniPlayer } = useMiniPlayer();
    const themeMode = useThemeModeContext();

    useEffect(() => {
        if (getCurrentUrl() === miniPlayer.parentLink) {
            setGlobalMiniPlayer({
                singleLoop: isLoop,
            });
        }
    }, [isLoop]);

    if (onClickMiniPlayer && !!miniPlayer.url) {
        return (
            <Box
                width={"100%"}
                display={"flex"}
                justifyContent={"end"}
            >
                <ControlIconsBox>
                    <IconButton onClick={onClickMiniPlayer}>
                        <StyledPictureInPictureIcon />
                    </IconButton>
                </ControlIconsBox>
            </Box>
        );
    }

    return (
        <Box display="flex">
            <ControlIconsBox>
                <StyledIconButton
                    onClick={() =>
                        handleChangeSeek(PlayerSeekAction.REWIND, 30)
                    }
                >
                    <StyledPlayerIcon
                        src={
                            themeMode.mode === "light"
                                ? rewind30Icon
                                : rewind30WIcon
                        }
                        alt="rewind 30 Sec"
                    />
                </StyledIconButton>
            </ControlIconsBox>
            <ControlIconsBox>
                <StyledIconButton
                    onClick={() =>
                        handleChangeSeek(PlayerSeekAction.REWIND, 15)
                    }
                >
                    <StyledPlayerIcon
                        src={
                            themeMode.mode === "light"
                                ? rewind15Icon
                                : rewind15WIcon
                        }
                        alt="rewind 15 Sec"
                    />
                </StyledIconButton>
            </ControlIconsBox>
            {onClickShuffle && (
                <ControlIconsBox onClick={onClickShuffle}>
                    <StyledIconButton>
                        <StyledMuiIcon>
                            {isShuffle ? (
                                <ShuffleOn fontSize={"large"} />
                            ) : (
                                <Shuffle fontSize={"large"} />
                            )}
                        </StyledMuiIcon>
                    </StyledIconButton>
                </ControlIconsBox>
            )}

            {onClickMultiLoop && (
                <ControlIconsBox onClick={onClickMultiLoop}>
                    <StyledIconButton>
                        <StyledMuiIcon>
                            {isMultiLoop ? (
                                <RepeatOn fontSize={"large"} />
                            ) : (
                                <Repeat fontSize={"large"} />
                            )}
                        </StyledMuiIcon>
                    </StyledIconButton>
                </ControlIconsBox>
            )}
            {onClickLoop && (
                <Box sx={{ padding: "8px 0" }} onClick={onClickLoop}>
                    <StyledIconButton>
                        <StyledMuiIcon>
                            {isLoop ? (
                                <RepeatOneOn fontSize={"large"} />
                            ) : (
                                <RepeatOneIcon fontSize={"large"} />
                            )}
                        </StyledMuiIcon>
                    </StyledIconButton>
                </Box>
            )}
            <ControlIconsBox>
                <StyledIconButton
                    onClick={() =>
                        handleChangeSeek(PlayerSeekAction.FORWARD, 30)
                    }
                >
                    <StyledPlayerIcon
                        src={
                            themeMode.mode === "light"
                                ? forward30Icon
                                : forward30WIcon
                        }
                        alt="forward 30 Sec"
                    />
                </StyledIconButton>
            </ControlIconsBox>
            <ControlIconsBox>
                <StyledIconButton
                    onClick={() =>
                        handleChangeSeek(PlayerSeekAction.FORWARD, 120)
                    }
                >
                    <StyledPlayerIcon
                        src={
                            themeMode.mode === "light"
                                ? forward2mIcon
                                : forward2mWIcon
                        }
                        alt="forward 120 Sec"
                    />
                </StyledIconButton>
            </ControlIconsBox>
            {onClickMiniPlayer && (
                <ControlIconsBox>
                    <StyledIconButton onClick={onClickMiniPlayer}>
                        <StyledPictureInPictureIcon />
                    </StyledIconButton>
                </ControlIconsBox>
            )}
        </Box>
    );
};

export interface PlayerActionBtnsProps extends InternalStandardProps<BoxProps> {
    isLoop?: boolean;
    isMultiLoop?: boolean;
    isShuffle?: boolean;
    onClickLoop?: () => void;
    onClickMultiLoop?: () => void;
    onClickShuffle?: () => void;
    handleChangeSeek: (seekType: PlayerSeekAction, seekTime: number) => void;
    onClickMiniPlayer?: () => void;
}
